// client/src/lib/api/returns-api.ts
// ===========================================
// MÓDULO DE GESTIÓN DE DEVOLUCIONES (RETURNS)
// Maneja validación, procesamiento y consulta de devoluciones.
// Usa apiRequest para inyectar headers y manejar errores de forma tipada.
// ===========================================

import { apiRequest, buildUrlWithParams } from "./api-client";
import type {
  Return,
  ReturnValidationResult,
  ProcessReturnDTO,
  ReturnsResponse,
} from "@/types/returns";

// -------------------------------------------
// Validar una venta para devolución
// -------------------------------------------
// COMENTARIO: Reemplazamos fetch manual por apiRequest.
// Los headers de autenticación se inyectan automáticamente.
export async function validateSaleForReturn(
  saleId: string,
): Promise<ReturnValidationResult> {
  return apiRequest<ReturnValidationResult>(
    `/api/returns/sale/${saleId}/validate`,
  );
}

// -------------------------------------------
// Procesar una devolución
// -------------------------------------------
// COMENTARIO: apiRequest maneja el POST y los errores.
// Mantenemos el tipo de retorno exacto para compatibilidad con componentes existentes.
export async function processReturn(data: ProcessReturnDTO): Promise<{
  success: boolean;
  returnRecord?: Return;
  error?: string;
}> {
  return apiRequest(`/api/returns`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// -------------------------------------------
// Obtener historial de devoluciones de una tienda
// -------------------------------------------
// COMENTARIO: Usamos buildUrlWithParams para construir la URL con filtros de forma segura.
// Eliminamos la lógica manual de URLSearchParams y la concatenación de query strings.
export async function getShopReturns(
  shopId: string,
  filters?: {
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  },
): Promise<ReturnsResponse> {
  // COMENTARIO: Añadimos shopId a los parámetros ya que es requerido por el backend
  const params = { shopId, ...filters };
  const url = buildUrlWithParams(`/api/returns`, params);
  return apiRequest<ReturnsResponse>(url);
}

// -------------------------------------------
// Obtener una devolución específica por ID
// -------------------------------------------
export async function getReturnById(returnId: string): Promise<{
  success: boolean;
  data?: Return;
  error?: string;
}> {
  return apiRequest(`/api/returns/${returnId}`);
}

// -------------------------------------------
// Obtener devoluciones de una venta específica
// -------------------------------------------
export async function getReturnsBySale(saleId: string): Promise<{
  success: boolean;
  data?: Return[];
  error?: string;
}> {
  return apiRequest(`/api/returns/sale/${saleId}`);
}
